'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useI18n } from '@/lib/i18n'
import { gameQueries } from '@/lib/queries'
import { trpcClient } from '@/lib/trpc'
import { ArrowLeftRight, Loader2, Play } from 'lucide-react'
import { useEffect, useState } from 'react'

type GameSettingType = string

interface TestSectionProps {
  response: string
  isLoading: boolean
  isComplete: boolean
  onReset: () => void
  onStimulateGame: () => void
  gameCount: number
  onGameCountChange: (count: number) => void
  selectedLanguage: string
  onLanguageChange: (language: string) => void
  gameSetting: GameSettingType
  onGameSettingChange: (setting: GameSettingType) => void
  goodModels: string[]
  onGoodModelsChange: (models: string[]) => void
  evilModels: string[]
  onEvilModelsChange: (models: string[]) => void
  batchGameCount: {
    validCombinations: number
    excludedCombinations: number
    totalGames: number
  } | null
  maxBatchGames: number
}

export function BatchSection({
  response,
  isLoading,
  isComplete,
  onReset,
  onStimulateGame,
  gameCount,
  onGameCountChange,
  selectedLanguage,
  onLanguageChange,
  gameSetting,
  onGameSettingChange,
  goodModels,
  onGoodModelsChange,
  evilModels,
  onEvilModelsChange,
  batchGameCount,
  maxBatchGames,
}: TestSectionProps) {
  const { t } = useI18n()
  const [gameSettingTypes, setGameSettingTypes] = useState<string[]>([])
  const [modelConfig, setModelConfig] = useState<Record<string, string[]>>({})

  useEffect(() => {
    const fetchInitial = async () => {
      const [settingsResult, configResult] = await Promise.all([
        trpcClient.queryRouter.getGameSettingTypes.query(),
        gameQueries.getModelConfig(),
      ])
      if (settingsResult.success)
        setGameSettingTypes(settingsResult.data)
      if (configResult.success && configResult.data)
        setModelConfig(configResult.data)
    }

    fetchInitial()
  }, [])

  const availableModels = Object.values(modelConfig).flat()

  const hasGoodModels = goodModels.length > 0
  const hasEvilModels = evilModels.length > 0
  const hasLanguage = selectedLanguage.length > 0
  const canSendRequest
    = hasGoodModels
      && hasEvilModels
      && hasLanguage
      && batchGameCount !== null
      && batchGameCount.totalGames <= maxBatchGames

  const handleGoodModelChange = (model: string, checked: boolean) => {
    if (checked) {
      onGoodModelsChange([...goodModels, model])
    }
    else {
      onGoodModelsChange(goodModels.filter(m => m !== model))
    }
  }

  const handleEvilModelChange = (model: string, checked: boolean) => {
    if (checked) {
      onEvilModelsChange([...evilModels, model])
    }
    else {
      onEvilModelsChange(evilModels.filter(m => m !== model))
    }
  }

  const handleSwapModels = () => {
    onGoodModelsChange(evilModels)
    onEvilModelsChange(goodModels)
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-20 dark:bg-slate-900 sm:px-6 lg:px-8">
      <div className="w-full">
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl lg:text-5xl">
            {t('admin.nav.stimulation')}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t('admin.description')}
          </p>
        </div>

        <div className="w-full">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="size-5" />
                {t('admin.cardTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              { }
              <div className="space-y-4 rounded-lg border bg-muted/30 p-4">
                { }
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="gameCount" className="text-sm font-medium">
                      {t('admin.config.gameCount')}
                    </Label>
                    <Input
                      id="gameCount"
                      type="number"
                      min="1"
                      max="100"
                      value={gameCount}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1
                        onGameCountChange(Math.max(1, Math.min(100, value)))
                      }}
                      placeholder={t('admin.config.selectGameCount')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="gameSetting"
                      className="text-sm font-medium"
                    >
                      {t('admin.config.gameSetting')}
                    </Label>
                    <Select
                      value={gameSetting}
                      onValueChange={onGameSettingChange}
                    >
                      <SelectTrigger id="gameSetting">
                        <SelectValue
                          placeholder={t('admin.config.selectGameSetting')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {gameSettingTypes.map(setting => (
                          <SelectItem key={setting} value={setting}>
                            {setting}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="language" className="text-sm font-medium">
                      {t('admin.config.language')}
                    </Label>
                    <Select
                      value={selectedLanguage}
                      onValueChange={onLanguageChange}
                    >
                      <SelectTrigger id="language">
                        <SelectValue
                          placeholder={t('admin.config.selectLanguage')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="zh">🇨🇳 简体中文</SelectItem>
                        <SelectItem value="en">🇺🇸 English</SelectItem>
                        <SelectItem value="fr">🇫🇷 Français</SelectItem>
                        <SelectItem value="ja">🇯🇵 日本語</SelectItem>
                        <SelectItem value="tw">🇭🇰 繁體中文</SelectItem>
                        <SelectItem value="es">🇪🇸 Español</SelectItem>
                        <SelectItem value="ar">🇸🇦 العربية</SelectItem>
                        <SelectItem value="ko">🇰🇷 한국어</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                { }
                <div className="space-y-4">
                  <div className="flex justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSwapModels}
                      className="flex items-center gap-1 px-3 py-2"
                      title={t('admin.config.swapModelsTitle')}
                    >
                      <ArrowLeftRight className="size-4" />
                      {t('admin.config.swapModels')}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">
                        {t('admin.config.goodModel')}{' '}
                        {goodModels.length > 0 && `(${goodModels.length} selected)`}
                      </Label>
                      <div className="max-h-80 overflow-y-auto rounded-md border p-3">
                        <div className="space-y-2">
                          {availableModels.map((model: string) => (
                            <div
                              key={model}
                              className="flex items-center space-x-2"
                            >
                              <Checkbox
                                id={`good-${model}`}
                                checked={goodModels.includes(model)}
                                onCheckedChange={checked =>
                                  handleGoodModelChange(model, checked as boolean)}
                              />
                              <Label
                                htmlFor={`good-${model}`}
                                className="cursor-pointer text-sm font-normal"
                              >
                                {model}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label className="text-sm font-medium">
                        {t('admin.config.evilModel')}{' '}
                        {evilModels.length > 0 && `(${evilModels.length} selected)`}
                      </Label>
                      <div className="max-h-80 overflow-y-auto rounded-md border p-3">
                        <div className="space-y-2">
                          {availableModels.map((model: string) => (
                            <div
                              key={model}
                              className="flex items-center space-x-2"
                            >
                              <Checkbox
                                id={`evil-${model}`}
                                checked={evilModels.includes(model)}
                                onCheckedChange={checked =>
                                  handleEvilModelChange(model, checked as boolean)}
                              />
                              <Label
                                htmlFor={`evil-${model}`}
                                className="cursor-pointer text-sm font-normal"
                              >
                                {model}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              { }
              <div className="flex flex-col items-center space-y-4 pt-4">
                <Button
                  onClick={onStimulateGame}
                  disabled={isLoading || !canSendRequest}
                  size="lg"
                  className="flex items-center gap-2 px-8 py-3 text-base"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="size-5 animate-spin" />
                      {t('admin.loading')}
                    </>
                  ) : (
                    <>
                      <Play className="size-5" />
                      {t('admin.buttonText')}
                    </>
                  )}
                </Button>
              </div>

              {hasGoodModels && hasEvilModels && (
                <div className="text-center text-sm text-muted-foreground">
                  <p>
                    {t('admin.config.totalGames')}: {batchGameCount
                      ? `${batchGameCount.validCombinations} × ${gameCount} = ${batchGameCount.totalGames} games (${batchGameCount.excludedCombinations} self-play excluded)`
                      : '—'}
                  </p>
                  {batchGameCount && batchGameCount.totalGames > maxBatchGames && (
                    <p className="mt-2 text-destructive" role="alert">
                      {t('admin.config.batchLimitExceeded')}
                    </p>
                  )}
                </div>
              )}

              {isComplete && (
                <div className="text-center">
                  <Button variant="outline" onClick={onReset} size="sm">
                    {t('admin.reset')}
                  </Button>
                </div>
              )}

              {isComplete && response && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/20">
                  <p className="mb-3 text-sm text-gray-900 dark:text-gray-100">
                    {response.split('\n')[0]}
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.response.gameIds')}:
                  </p>
                  <div className="mt-2 space-y-1">
                    {(() => {
                      const lines = response.split('\n')
                      const gameIdsLine = lines.find(line => line.includes(t('admin.response.gameIds')))
                      if (gameIdsLine) {
                        const gameIdsText = gameIdsLine.split(':')[1]?.trim()
                        if (gameIdsText) {
                          return gameIdsText.split(', ').map((gameId, index) => (
                            <div key={index} className="mb-1 mr-1 inline-block rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                              {gameId.trim()}
                            </div>
                          ))
                        }
                      }
                      return <div className="text-xs text-muted-foreground">No game IDs found</div>
                    })()}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
